import { EventEmitter, SimpleChanges } from '@angular/core';
import { DEFAULT_ANNOTATION_FILL_COLOR, DEFAULT_ANNOTATION_FONT_SIZE, DEFAULT_ANNOTATION_LINE_COLOR, DEFAULT_ANNOTATION_LINE_WEIGHT, DEFAULT_ANNOTATION_TEXT_COLOR, FUND_CHART_ANNOTATION_KEY_TO_DISPLAY_NAME, } from 'ChartApp/ts/apps/fund-chart/constants';
import { FundChartAnnotationName, FundChartCursorType } from 'ChartApp/ts/enums/fund-chart-common.enum';
import { PromptModalComponent } from 'Angular/features/components/modals/prompt-modal/prompt-modal.component';
import { memoizeOne } from 'Angular/utils/memoize-one';
import * as i0 from "@angular/core";
import * as i1 from "../../../../../../../../../ycharts/static/ts/features/directives/icon/icon.directive";
import * as i2 from "../../../../../../../../../ycharts/static/ts/features/components/modals/prompt-modal/prompt-modal.component";
import * as i3 from "../../../../../../../../../ycharts/static/ts/features/components/dropdowns/simple-dropdown/simple-dropdown.component";
import * as i4 from "ChartApp/ts/apps/fund-chart/components/fund-chart-annotation-styling-toolbar/fund-chart-annotation-styling-toolbar.component";
const _c0 = ["editLabelTextModal"];
/**
 * Fund Chart Toolset Component
 *
 * This component handles the controls for adding/editing chart annotations and choosing the chart cursor type.
 *
 * Input Properties:
 *  selectedAnnotation: chart annotation that was most recently clicked
 *  showCrosshair: whether to use the crosshair cursor on the chart
 *
 * Output Properties:
 *  cursorChangeHandler: emits when a cursor option is clicked
 *  annotationStyleChangeHandler: emits the updated annotation when the style is changed with the annotation styling toolbar.
 */
export class FundChartToolsetComponent {
    constructor() {
        this.selectedAnnotation = null;
        this.showCrosshair = false;
        this.cursorChangeHandler = new EventEmitter();
        this.annotationStyleChangeHandler = new EventEmitter();
        this.deleteAnnotationHandler = new EventEmitter();
        this.toolsetOpen = false;
        this.selectedAnnotationName = null;
        this.selectedAnnotationType = null;
        this.selectedFontSize = DEFAULT_ANNOTATION_FONT_SIZE;
        this.selectedTextColor = DEFAULT_ANNOTATION_TEXT_COLOR;
        this.selectedLineWeight = DEFAULT_ANNOTATION_LINE_WEIGHT;
        this.selectedLineColor = DEFAULT_ANNOTATION_LINE_COLOR;
        this.selectedFillColor = DEFAULT_ANNOTATION_FILL_COLOR;
        this.cursorDropdownConfig = {
            title: {
                label: '',
                icon: 'icon-tool-cursor',
                iconClass: 'tool-icon',
            },
            list: [
                {
                    type: 'option',
                    label: 'Cursor',
                    icon: 'icon-tool-cursor',
                    value: FundChartCursorType.CURSOR,
                },
                {
                    type: 'option',
                    label: 'Crosshair',
                    icon: 'icon-tool-crosshair',
                    value: FundChartCursorType.CROSSHAIR,
                },
            ],
        };
        this.shapeAnnotationDropdownConfig = {
            title: {
                label: '',
                icon: 'icon-tool-circle',
                iconClass: 'tool-icon',
            },
            list: [
                {
                    type: 'option',
                    label: FundChartAnnotationName.CIRCLE,
                    icon: 'icon-tool-circle',
                    value: FundChartAnnotationName.CIRCLE,
                    dropdownItemClass: 'highcharts-circle-annotation',
                },
                {
                    type: 'option',
                    label: FundChartAnnotationName.RECTANGLE,
                    icon: 'icon-tool-rectangle',
                    value: FundChartAnnotationName.RECTANGLE,
                    dropdownItemClass: 'highcharts-rectangle-annotation',
                },
            ],
        };
        this.lineAnnotationDropdownConfig = {
            title: {
                label: '',
                icon: 'icon-tool-line',
                iconClass: 'tool-icon',
            },
            list: [
                {
                    type: 'option',
                    label: FundChartAnnotationName.TREND_SEGMENT,
                    icon: 'icon-tool-trend-segment',
                    value: FundChartAnnotationName.TREND_SEGMENT,
                    dropdownItemClass: 'highcharts-segment',
                },
                {
                    type: 'option',
                    label: FundChartAnnotationName.ARROW_SEGMENT,
                    icon: 'icon-tool-arrow-segment',
                    value: FundChartAnnotationName.ARROW_SEGMENT,
                    dropdownItemClass: 'highcharts-arrow-segment',
                },
                {
                    type: 'option',
                    label: FundChartAnnotationName.RAY,
                    icon: 'icon-tool-ray',
                    value: FundChartAnnotationName.RAY,
                    dropdownItemClass: 'highcharts-ray',
                },
                {
                    type: 'option',
                    label: FundChartAnnotationName.ARROW_RAY,
                    icon: 'icon-tool-arrow-ray',
                    value: FundChartAnnotationName.ARROW_RAY,
                    dropdownItemClass: 'highcharts-arrow-ray',
                },
                {
                    type: 'option',
                    label: FundChartAnnotationName.LINE,
                    icon: 'icon-tool-line',
                    value: FundChartAnnotationName.LINE,
                    dropdownItemClass: 'highcharts-infinity-line',
                },
                {
                    type: 'option',
                    label: FundChartAnnotationName.ARROW_LINE,
                    icon: 'icon-tool-arrow-line',
                    value: FundChartAnnotationName.ARROW_LINE,
                    dropdownItemClass: 'highcharts-arrow-infinity-line',
                },
                {
                    type: 'option',
                    label: FundChartAnnotationName.PARALLEL_CHANNEL,
                    icon: 'icon-tool-parallel-channel',
                    value: FundChartAnnotationName.PARALLEL_CHANNEL,
                    dropdownItemClass: 'highcharts-parallel-channel',
                },
                {
                    type: 'option',
                    label: FundChartAnnotationName.HORIZONTAL_LINE,
                    icon: 'icon-tool-horizontal-line',
                    value: FundChartAnnotationName.HORIZONTAL_LINE,
                    dropdownItemClass: 'highcharts-horizontal-line',
                },
                {
                    type: 'option',
                    label: FundChartAnnotationName.VERTICAL_LINE,
                    icon: 'icon-tool-vertical-line',
                    value: FundChartAnnotationName.VERTICAL_LINE,
                    dropdownItemClass: 'highcharts-vertical-line',
                },
            ],
        };
        this.forceUpdateCurrentLabelText = false;
        this.annotationLabelText = memoizeOne((annotation, _forceUpdate) => {
            if (annotation?.chartAnnotation.labels?.length) {
                const labelText = annotation.chartAnnotation.labels[0].text;
                if (labelText) {
                    return labelText;
                }
                const labelPoint = annotation.chartAnnotation.labels[0].point ?? {};
                if (typeof labelPoint !== 'string' && 'y' in labelPoint && typeof labelPoint.y === 'number') {
                    // Round to 2 decimal places
                    return (Math.round((labelPoint.y + Number.EPSILON) * 100) / 100).toString();
                }
            }
            return '';
        });
    }
    ngOnChanges(changes) {
        if (changes.selectedAnnotation?.currentValue === null) {
            this.selectedAnnotationType = null;
            this.selectedAnnotationName = null;
            this.selectedFontSize = DEFAULT_ANNOTATION_FONT_SIZE;
            this.selectedTextColor = DEFAULT_ANNOTATION_TEXT_COLOR;
            this.selectedLineWeight = DEFAULT_ANNOTATION_LINE_WEIGHT;
            this.selectedLineColor = DEFAULT_ANNOTATION_LINE_COLOR;
            this.selectedFillColor = DEFAULT_ANNOTATION_FILL_COLOR;
        }
        else if (changes.selectedAnnotation?.currentValue) {
            const { langKey, type } = this.selectedAnnotation.chartAnnotation;
            if (langKey === 'label' && type === 'basicAnnotation') {
                this.selectedFontSize = this.getFontSize(this.selectedAnnotation.chartAnnotation);
                this.selectedTextColor = this.getTextColor(this.selectedAnnotation.chartAnnotation);
                this.selectedFillColor = this.getFillColor(this.selectedAnnotation.chartAnnotation);
                this.selectedAnnotationType = 'text';
            }
            else {
                this.selectedLineWeight = this.getLineWeight(this.selectedAnnotation.chartAnnotation);
                this.selectedLineColor = this.getLineColor(this.selectedAnnotation.chartAnnotation);
                this.selectedAnnotationType = 'line_or_shape';
            }
            this.selectedAnnotationName = FUND_CHART_ANNOTATION_KEY_TO_DISPLAY_NAME[langKey];
        }
    }
    toggleToolset() {
        this.toolsetOpen = !this.toolsetOpen;
    }
    selectCursorType(cursorType) {
        const showCrosshair = cursorType === FundChartCursorType.CROSSHAIR;
        this.cursorChangeHandler.emit({ showCrosshair });
        this.toggleToolset();
    }
    getFontSize(annotation) {
        if (annotation.labels?.length) {
            return annotation.labels[0].style?.fontSize ?? DEFAULT_ANNOTATION_FONT_SIZE;
        }
        return DEFAULT_ANNOTATION_FONT_SIZE;
    }
    getTextColor(annotation) {
        if (annotation.labels?.length) {
            return annotation.labels[0].style?.color ?? DEFAULT_ANNOTATION_TEXT_COLOR;
        }
        return DEFAULT_ANNOTATION_TEXT_COLOR;
    }
    getFillColor(annotation) {
        return annotation.labelOptions?.backgroundColor ?? DEFAULT_ANNOTATION_FILL_COLOR;
    }
    getLineWeight(annotation) {
        return `${annotation.shapeOptions?.strokeWidth ?? DEFAULT_ANNOTATION_LINE_WEIGHT}`;
    }
    getLineColor(annotation) {
        return `${annotation.shapeOptions?.stroke ?? DEFAULT_ANNOTATION_LINE_COLOR}`;
    }
    updateFontSize(newFontSize) {
        this.selectedFontSize = newFontSize;
        if (this.selectedAnnotation && this.selectedAnnotation.chartAnnotation.labels?.length) {
            const { style } = this.selectedAnnotation.chartAnnotation.labels[0];
            this.selectedAnnotation.chartAnnotation.labels[0].style = { ...style, fontSize: newFontSize };
            this.annotationStyleChangeHandler.emit(this.selectedAnnotation);
        }
    }
    updateTextColor(newTextColor) {
        this.selectedTextColor = newTextColor;
        if (this.selectedAnnotation && this.selectedAnnotation.chartAnnotation.labels?.length) {
            const { style } = this.selectedAnnotation.chartAnnotation.labels[0];
            this.selectedAnnotation.chartAnnotation.labels[0].style = { ...style, color: newTextColor };
            this.annotationStyleChangeHandler.emit(this.selectedAnnotation);
        }
    }
    updateFillColor(newColor) {
        this.selectedFillColor = newColor;
        if (this.selectedAnnotation) {
            this.selectedAnnotation.chartAnnotation.labelOptions.backgroundColor = newColor;
            this.annotationStyleChangeHandler.emit(this.selectedAnnotation);
        }
    }
    updateLabelText(newText) {
        if (this.selectedAnnotation && this.selectedAnnotation.chartAnnotation.labels?.length) {
            delete this.selectedAnnotation.chartAnnotation.labelOptions?.format;
            this.selectedAnnotation.chartAnnotation.labels[0].text = newText;
            this.annotationStyleChangeHandler.emit(this.selectedAnnotation);
            this.editLabelTextModal.closeModal();
            this.forceUpdateCurrentLabelText = !this.forceUpdateCurrentLabelText;
        }
    }
    updateLineColor(newLineColor) {
        this.selectedLineColor = newLineColor;
        if (this.selectedAnnotation) {
            const { typeOptions } = this.selectedAnnotation.chartAnnotation;
            this.selectedAnnotation.chartAnnotation.shapeOptions = {
                ...this.selectedAnnotation.chartAnnotation.shapeOptions,
                stroke: newLineColor,
            };
            if (typeOptions) {
                typeOptions.line = {
                    ...typeOptions.line,
                    fill: newLineColor,
                };
            }
            this.annotationStyleChangeHandler.emit(this.selectedAnnotation);
        }
    }
    updateLineWeight(newLineWeight) {
        this.selectedLineWeight = newLineWeight;
        if (this.selectedAnnotation) {
            const { typeOptions } = this.selectedAnnotation.chartAnnotation;
            this.selectedAnnotation.chartAnnotation.shapeOptions = {
                ...this.selectedAnnotation.chartAnnotation.shapeOptions,
                strokeWidth: Number.parseInt(newLineWeight, 10),
            };
            if (typeOptions) {
                typeOptions.line = {
                    ...typeOptions.line,
                    strokeWidth: Number.parseInt(newLineWeight, 10),
                };
            }
            this.annotationStyleChangeHandler.emit(this.selectedAnnotation);
        }
    }
}
FundChartToolsetComponent.ɵfac = function FundChartToolsetComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || FundChartToolsetComponent)(); };
FundChartToolsetComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: FundChartToolsetComponent, selectors: [["ycn-fund-chart-toolset"]], viewQuery: function FundChartToolsetComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(_c0, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.editLabelTextModal = _t.first);
    } }, inputs: { selectedAnnotation: "selectedAnnotation", showCrosshair: "showCrosshair" }, outputs: { cursorChangeHandler: "cursorChangeHandler", annotationStyleChangeHandler: "annotationStyleChangeHandler", deleteAnnotationHandler: "deleteAnnotationHandler" }, standalone: false, features: [i0.ɵɵNgOnChangesFeature], decls: 11, vars: 27, consts: [["editLabelTextModal", ""], [1, "toolbar", "toolbar-vertical", "toolbar-collapsible", "toolbar-left-center", "toolset"], [1, "toolbar-toggler", "btn", "btn-secondary", 3, "click"], [1, "tool", "dropdown", 3, "handler", "buttonClass", "config", "dropdownClass"], [1, "tool"], [1, "btn", "btn-secondary", "highcharts-label-annotation"], ["ycnIcon", "", "role", "img", 1, "tool-icon", 3, "name"], [1, "tool", "dropdown", 3, "buttonClass", "config", "dropdownClass", "closeOnSubmit"], [1, "tool", "dropdown", "static", 3, "buttonClass", "config", "dropdownClass", "closeOnSubmit"], [3, "deleteHandler", "editTextHandler", "fillColorChangeHandler", "fontSizeChangeHandler", "lineColorChangeHandler", "lineWeightChangeHandler", "textColorChangHandler", "id", "hidden", "selectedAnnotationName", "selectedAnnotationType", "selectedFillColor", "selectedFontSize", "selectedLineColor", "selectedLineWeight", "selectedTextColor"], [3, "promptHandler", "clearInputOnClose", "promptHeader", "promptInputText", "promptPlaceholder"]], template: function FundChartToolsetComponent_Template(rf, ctx) { if (rf & 1) {
        const _r1 = i0.ɵɵgetCurrentView();
        i0.ɵɵelementStart(0, "div", 1)(1, "div", 2);
        i0.ɵɵlistener("click", function FundChartToolsetComponent_Template_div_click_1_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.toggleToolset()); });
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(2, "ycn-simple-dropdown", 3);
        i0.ɵɵlistener("handler", function FundChartToolsetComponent_Template_ycn_simple_dropdown_handler_2_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.selectCursorType($event)); });
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(3, "div", 4)(4, "div", 5);
        i0.ɵɵnamespaceSVG();
        i0.ɵɵelement(5, "svg", 6);
        i0.ɵɵelementEnd()();
        i0.ɵɵnamespaceHTML();
        i0.ɵɵelement(6, "ycn-simple-dropdown", 7)(7, "ycn-simple-dropdown", 8);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(8, "ycn-fund-chart-annotation-styling-toolbar", 9);
        i0.ɵɵlistener("deleteHandler", function FundChartToolsetComponent_Template_ycn_fund_chart_annotation_styling_toolbar_deleteHandler_8_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.deleteAnnotationHandler.emit()); })("editTextHandler", function FundChartToolsetComponent_Template_ycn_fund_chart_annotation_styling_toolbar_editTextHandler_8_listener() { i0.ɵɵrestoreView(_r1); const editLabelTextModal_r2 = i0.ɵɵreference(10); return i0.ɵɵresetView(editLabelTextModal_r2.openModal()); })("fillColorChangeHandler", function FundChartToolsetComponent_Template_ycn_fund_chart_annotation_styling_toolbar_fillColorChangeHandler_8_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.updateFillColor($event)); })("fontSizeChangeHandler", function FundChartToolsetComponent_Template_ycn_fund_chart_annotation_styling_toolbar_fontSizeChangeHandler_8_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.updateFontSize($event)); })("lineColorChangeHandler", function FundChartToolsetComponent_Template_ycn_fund_chart_annotation_styling_toolbar_lineColorChangeHandler_8_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.updateLineColor($event)); })("lineWeightChangeHandler", function FundChartToolsetComponent_Template_ycn_fund_chart_annotation_styling_toolbar_lineWeightChangeHandler_8_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.updateLineWeight($event)); })("textColorChangHandler", function FundChartToolsetComponent_Template_ycn_fund_chart_annotation_styling_toolbar_textColorChangHandler_8_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.updateTextColor($event)); });
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(9, "ycn-prompt-modal", 10, 0);
        i0.ɵɵlistener("promptHandler", function FundChartToolsetComponent_Template_ycn_prompt_modal_promptHandler_9_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.updateLabelText($event)); });
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵclassMap(ctx.toolsetOpen ? "is-opened" : "");
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("buttonClass", "btn btn-secondary")("config", ctx.cursorDropdownConfig)("dropdownClass", "dropdown-menu-with-icons");
        i0.ɵɵadvance(3);
        i0.ɵɵproperty("name", "icon-tool-text");
        i0.ɵɵadvance();
        i0.ɵɵproperty("buttonClass", "btn btn-secondary")("config", ctx.shapeAnnotationDropdownConfig)("dropdownClass", "dropdown-menu-with-icons")("closeOnSubmit", false);
        i0.ɵɵadvance();
        i0.ɵɵproperty("buttonClass", "btn btn-secondary")("config", ctx.lineAnnotationDropdownConfig)("dropdownClass", "dropdown-menu-with-icons")("closeOnSubmit", false);
        i0.ɵɵadvance();
        i0.ɵɵproperty("id", "annotation-styling-toolbar")("hidden", !(ctx.selectedAnnotation && ctx.selectedAnnotationType && ctx.selectedAnnotationName))("selectedAnnotationName", ctx.selectedAnnotationName)("selectedAnnotationType", ctx.selectedAnnotationType)("selectedFillColor", ctx.selectedFillColor)("selectedFontSize", ctx.selectedFontSize)("selectedLineColor", ctx.selectedLineColor)("selectedLineWeight", ctx.selectedLineWeight)("selectedTextColor", ctx.selectedTextColor);
        i0.ɵɵadvance();
        i0.ɵɵproperty("clearInputOnClose", false)("promptHeader", "Edit Label")("promptInputText", ctx.annotationLabelText(ctx.selectedAnnotation, ctx.forceUpdateCurrentLabelText))("promptPlaceholder", "Enter label text");
    } }, dependencies: [i1.IconDirective, i2.PromptModalComponent, i3.SimpleDropdownComponent, i4.FundChartAnnotationStylingToolbarComponent], encapsulation: 2 });
